import { useState } from 'react';
import { saveJournalNote } from 'zite-endpoints-sdk';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { StickyNote, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourcePage: string;
  sourcePath: string;
  defaultTitle?: string;
}

export default function AddNoteDialog({ open, onOpenChange, sourcePage, sourcePath, defaultTitle }: Props) {
  const [form, setForm] = useState({ title: defaultTitle || '', note: '', isQuestion: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim() || !form.note.trim()) { toast.error('Title and note are required'); return; }
    setSaving(true);
    try {
      await saveJournalNote({ title: form.title, content: form.note, sourcePage, sourcePath, isQuestion: form.isQuestion });
      setSaved(true);
      toast.success('Note saved to your journal');
      setTimeout(() => {
        setSaved(false);
        setForm({ title: defaultTitle || '', note: '', isQuestion: false });
        onOpenChange(false);
      }, 1200);
    } catch (e: any) { toast.error(e.message || 'Failed to save note'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!saving) { onOpenChange(v); if (!v) setSaved(false); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-primary" /> Add Journal Note
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="bg-muted/30 rounded-lg px-3 py-2">
            <p className="text-xs text-muted-foreground">Linked to</p>
            <p className="text-sm font-medium text-foreground">{sourcePage}</p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Note Title *</label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={`Note about ${sourcePage}`} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Your Note *</label>
            <Textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Write your thoughts, questions, or reminders here…" className="min-h-28" />
          </div>
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
            <Switch id="isq" checked={form.isQuestion} onCheckedChange={v => setForm(f => ({ ...f, isQuestion: v }))} />
            <div>
              <Label htmlFor="isq" className="text-sm font-medium cursor-pointer">This is a question for my manager</Label>
              <p className="text-xs text-muted-foreground">You'll be able to send this to your manager from the Journal</p>
            </div>
          </div>
          {saved ? (
            <div className="flex items-center justify-center gap-2 py-2 text-primary">
              <CheckCircle className="w-5 h-5" /><span className="text-sm font-medium">Saved to journal!</span>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button className="flex-1" onClick={handleSave} disabled={saving}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save Note to Journal'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
